<template>
  <v-flex xs12>
    <v-card>
      <v-toolbar class="secondary lighten-2" dark>
        <v-toolbar-title>
          <span>{{ $t('measures_title')}}:</span>
          <span v-if="currentData.length">{{ currentData.length }}</span>
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <v-toolbar-title v-if="currentData.length"></v-toolbar-title>
        <v-badge color="primary" overlap>
          <template v-slot:badge>
            <span v-if="filterCount">{{filterCount}}</span>
          </template>
          <v-btn tile @click="drawerRightVisible = !drawerRightVisible">
            <v-icon color="primary">filter_list</v-icon>
          </v-btn>
        </v-badge>
      </v-toolbar>
      <v-data-table
        :headers="headers"
        :items="currentData"
        :pagination.sync="listConfigPaginationDataTable"
        hide-actions
        class="elevation-1"
        id="paginationstart"
      >
        <template slot="items" slot-scope="props">
          <td>{{props.item.autor}}</td>
          <td>
            <router-link
              :to="{name:'audit', params: {auditId: props.item.audit_id}}"
            >{{props.item.audit_name}}</router-link>
          </td>
          <td>{{props.item.assignedTo}}</td>
          <td>{{props.item.text}}</td>
          <td>
            <SnippetDate :items="props.item.dueDate" />
          </td>
          <td>{{props.item.status}}</td>
          <td class="justify-center layout px-0">
            <v-btn icon class="mx-0" @click="updateMeasure(props.item.id)">
              <v-icon color="teal">edit</v-icon>
            </v-btn>
            <v-btn
              icon
              class="mx-0"
              @click="deleteCurrentMeasure(props.item.audit_id, props.item.audititem_id, props.item.deviation_id, props.item.id)"
            >
              <v-icon color="pink">delete</v-icon>
            </v-btn>
          </td>
        </template>
        <template slot="no-data">{{ $t('measure_matrix_nodata')}}</template>
        <template slot="page-text" slot-scope="props">{{ props }}</template>
      </v-data-table>

      <drawer-right
        :drawerRightVisible="drawerRightVisible"
        @toggleDrawer="drawerRightVisible = !drawerRightVisible"
      >
        <ItemsJsFacets
          slot="content"
          :per-page="listConfigPageSize"
          :page="listConfigPage"
          :configuration="listConfigItemsJs"
          :rows="currentMeasures"
          @itemsjs-result-list-changed="onIitemsjsResultListChanged"
        ></ItemsJsFacets>
      </drawer-right>
      <PaginationControl
        v-if="listConfigPaginationRequired"
        :page-sizes="listConfigPageSizes"
        :length="listConfigPageCount"
        @change="listConfigOnPaginationChange"
      />
    </v-card>
  </v-flex>
</template>

<i18n>
{
  "de": {
    "measures_title": "Maßnahmen",
    "created_header": "Angelegt durch",
    "audit_header": "Audit",
    "duedate_header": "Zu erledigen bis",
    "createddate_label": "Erstellt am",
    "autor_label": "Autor",
    "assigned_to_header": "Zugewiesen an",
    "measure_header": "Maßnahme",
    "status_header": "Status",
    "actions_header": "Aktionen",
    "measure_matrix_nodata": "Keine Ergebnisse"
  },
  "en": {
    "auditfinding_title": "Audit Finding",
    "auditfinding_label": "Description of the Finding",
    "measure_matrix_nodata": "No data"
    
  }
}
</i18n>
  
<script>
// Snippets/Components
import SnippetDate from "@/components/snippets/SnippetDate";
import ItemsJsFacets from "@/components/controls/ItemsJsFacets.vue";
import PaginationControl from "@/components/controls/PaginationControl";

import ListConfigurationMixin from "@/components/mixins/ListConfigurationMixin";
// Layouts
import DrawerRight from "@/components/layouts/BaseLayouts/DrawerRight.vue";

import { mapGetters, mapActions } from "vuex";
import { api } from "@/store/modules/audits";
import { api as confApi } from "@/store/modules/configuration";

export default {
  name: "MeasureListWidget",
  props: {
    auditId: {
      type: String,
      default: null
    },
    listConfigKey: {
      type: String,
      required: true
    }
  },
  data: () => ({
    filterCount: 0,
    drawerRightVisible: false,
    currentData: [],
    dialogMeasure: false,
    today: new Date().toISOString().slice(0, 10),
    currentFilter: "all"
  }),
  components: {
    DrawerRight,
    ItemsJsFacets,
    SnippetDate,
    PaginationControl
  },
  mixins: [ListConfigurationMixin],
  computed: {
    ...mapGetters(api.namespace, [
      api.getters.getFlatMeasureMatrix,
      api.getters.getMeasureById
    ]),
    ...mapGetters(confApi.namespace, [confApi.getters.getMeasureActions]),
    headers() {
      return [
        { text: this.$t("created_header"), value: "autor", sortable: true },
        { text: this.$t("audit_header"), value: "audit_name", sortable: true },
        {
          text: this.$t("assigned_to_header"),
          value: "assignedTo",
          sortable: true
        },
        { text: this.$t("measure_header"), value: "text", sortable: true },
        { text: this.$t("duedate_header"), value: "dueDate", sortable: true },
        { text: this.$t("status_header"), value: "status", sortable: true },
        { text: this.$t("actions_header"), value: "id", sortable: false }
      ];
    },

    currentMeasures() {
      return this.getFlatMeasureMatrix(this.auditId);
    },

    // Overwrites from ListConfigurationMixin
    listConfigConfigKey() {
      return this.listConfigKey;
    },
    listConfigTotalItemCount() {
      return this.currentData.length;
    }
  },
  methods: {
    ...mapActions(api.namespace, [
      api.actions.setAuditId,
      api.actions.setAuditItemId,
      api.actions.setDeviationId,
      api.actions.setMeasureId,
      api.actions.setDialogItem,
      api.actions.setDialogAction,
      api.actions.deleteMeasure
    ]),
    onIitemsjsResultListChanged(d, filter, query) {
      this.filterCount =
        (query === "" ? 0 : 1) +
        Object.values(filter).reduce((prev, item) => prev + item.length, 0);
      this.currentData = d;
    },
    setFilter(filter) {
      this.currentFilter = filter || "all";
    },
    filter: function(items, query) {
      return items.filter(function(item) {
        if (query === "status=offen") {
          if (item.status === "Offen") {
            return item;
          }
        } else if (query === "status=geschlossen") {
          if (item.status === "Abgeschlossen") {
            return item;
          }
        } else if (query === "!assigned_to") {
          if (!item.assignedTo) {
            return item;
          }
        } else {
          return item;
        }
      });
    },
    updateMeasure(measure_id) {
      console.log("Setting Object Path");
      const measure = this.getMeasureById(measure_id);

      this.setAuditId(measure.audit_id);
      this.setAuditItemId(measure.audititem_id);
      this.setDeviationId(measure.deviation_id);
      this.setMeasureId(measure.id);
      this.setDialogAction("update");

      console.log("Update Measure");
      this.setDialogItem(measure);
      this.dialogMeasure = true;
      this.$router.push({ name: "measuredialog", params: { id: measure_id } });
    },
    deleteCurrentMeasure(audit_id, audititem_id, deviation_id, measure_id) {
      //this.setAuditId(audit_id);
      //this.setAuditItemId(audititem_id);
      //this.setDeviationId(deviation_id);

      const payload = {
        audit_id: audit_id,
        audititem_id: audititem_id,
        deviation_id: deviation_id,
        measure_id: measure_id
      };

      console.log("Delete measure");

      confirm("Are you sure you want to delete this measure?") &&
        this.deleteMeasure(payload);
    }
  }
};
</script>
